import { useNavigate } from 'react-router-dom';
import { allSwitchOptions } from '../config';
import { ReportOptions } from './ReportOptions';
import styles from './ReportSwitcher.module.css';

/*
 * Report switcher. Lets a user with access to multiple reports jump between
 * them. Renders nothing when the user has 0–1 accessible reports — a single-
 * report user never sees a switcher.
 *
 * Options are the configs the user's token allows: running campaigns under
 * "Active", ended ones under "Archived", the newest first in each. Selecting
 * one navigates to that report's route; RequireAuth + DashboardPage already
 * gate access, so this only ever offers reachable destinations.
 *
 * Admins (isAdmin) carry no `allowed_reports` but may open every report, so
 * they're offered all configured dashboards.
 */
type Props = {
  /** report ids from the JWT's allowed_reports */
  allowedReports: ReadonlyArray<string>;
  /** the report currently being viewed */
  currentReportId: string;
  /** role=admin — bypasses the allow-list and sees every report */
  isAdmin?: boolean;
};

export function ReportSwitcher({ allowedReports, currentReportId, isAdmin = false }: Props) {
  const navigate = useNavigate();
  const options = allSwitchOptions(allowedReports, isAdmin);

  // Single (or no) report → nothing to switch between.
  if (options.length <= 1) return null;

  return (
    <label className={styles.wrap}>
      <span className={styles.srOnly}>Switch report</span>
      <select
        className={styles.select}
        value={currentReportId}
        onChange={(e) => {
          const next = options.find((c) => c.reportId === e.target.value);
          if (next) navigate(next.path);
        }}
      >
        <ReportOptions options={options} labels={{ active: 'Active', archived: 'Archived' }} value={(o) => o.reportId} />
      </select>
      <span className={styles.caret} aria-hidden>
        ▾
      </span>
    </label>
  );
}
